import React, { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  PageHeader,
  Card,
  Table,
  Button,
  FilterContainer,
  RangePicker,
  Banner,
  Dropdown,
  Chevron
} from '@lmc/ui-kit'
import { LineChart } from '@lmc/ui-kit'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import dayjs from 'dayjs'
import { colors } from '@lmc/common/constants'
import { mockMedication, mockLogs } from './mockData'
import PageWrapper from '../componentsV2/PageWrapper'
import { DownloadOutlined } from '@ant-design/icons'
import ExternalLink from 'components/icons/ExternalLink'
import useCarers from 'screens/Organisation/screens/TeamList/hooks/useCarers'
import { createTableColumns } from './TableColumns'
import StockCheckModal, { StockCheckData } from '../modalsV2/StockCheckModal'
import DeliveryModal, { DeliveryData } from '../modalsV2/DeliveryModal'
import DisposalModal, { DisposalData } from '../modalsV2/DisposalModal'
import { StockRecordDrawer } from './StockRecordDrawer/StockRecordDrawer'

const EMARMedicationStockScreenV2: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const navigate = useNavigate()
  const { id: residentId, medication_id: medicationId } = useParams()
  const { carers } = useCarers()

  const [medication] = useState(mockMedication)
  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [selectedRecordType, setSelectedRecordType] = useState<
    'view' | 'edit' | 'administration' | 'comment'
  >('view')

  const allLogs = mockLogs.map((log, index) => ({
    ...log,
    loggedBy: carers[index % carers.length] || carers[0] || log.loggedBy
  }))

  const sortedLogs = allLogs.sort(
    (a, b) => dayjs(b.timeLogged).valueOf() - dayjs(a.timeLogged).valueOf()
  )
  const last10Logs = sortedLogs.slice(0, 10)

  const getDateRange = () => {
    if (last10Logs.length === 0) {
      return {
        start: dayjs().startOf('day'),
        end: dayjs().endOf('day')
      }
    }

    const oldestLog = last10Logs[last10Logs.length - 1]
    const newestLog = last10Logs[0]

    return {
      start: dayjs(oldestLog.timeLogged).startOf('day'),
      end: dayjs(newestLog.timeLogged).endOf('day')
    }
  }

  const dateRange = getDateRange()
  const [startDate, setStartDate] = useState(dateRange.start)
  const [endDate, setEndDate] = useState(dateRange.end)

  const logs = last10Logs.filter((log) => {
    const logDate = dayjs(log.timeLogged)
    return (
      logDate.isAfter(startDate.subtract(1, 'day')) &&
      logDate.isBefore(endDate.add(1, 'day'))
    )
  })

  const showAlert = medication.currentStock <= medication.lowStockThreshold

  const chartData = [
    {
      name: 'Stock Count',
      data: logs
        .filter((log) => !log.deleted && log.amount1 !== undefined)
        .map((log) => log.amount1)
        .sort((a, b) => a - b)
    }
  ]

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0])
      setEndDate(dates[1])
    } else {
      setStartDate(dayjs().subtract(14, 'days'))
      setEndDate(dayjs())
    }
  }

  const handleStockAction = (action: string) => {
    if (action === 'Stock Check') {
      setStockCheckModalOpen(true)
    } else if (action === 'New Delivery Received') {
      setDeliveryModalOpen(true)
    } else if (action === 'Disposal') {
      setDisposalModalOpen(true)
    } else {
      console.log(`${action} clicked`)
    }
  }

  const handleStockCheckSave = (data: StockCheckData) => {
    console.log('Stock check data:', data)
    setStockCheckModalOpen(false)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
  }

  const handleDeliverySave = (data: DeliveryData) => {
    console.log('Delivery data:', data)
    setDeliveryModalOpen(false)
  }

  const handleDeliveryCancel = () => {
    setDeliveryModalOpen(false)
  }

  const handleDisposalSave = (data: DisposalData) => {
    console.log('Disposal data:', data)
    if (data.hasDiscrepancy && data.discrepancyReason) {
      console.log('Creating Stock Check record (resolved) and Disposal record')
    }
    setDisposalModalOpen(false)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
  }

  const handleDownloadPDF = () => {
    console.log('Download PDF clicked')
  }

  const handleReadMore = () => {
    window.open(
      'https://support.logmycare.co.uk/en/collections/2957232-stock',
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setSelectedRecordType('view')
    setDrawerOpen(true)
  }

  const handleEditRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setSelectedRecordType('edit')
    setDrawerOpen(true)
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setSelectedRecordType('administration')
    setDrawerOpen(true)
  }

  const handleViewCommentRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setSelectedRecordType('comment')
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const selectedRecord = logs.find((log) => log.id === selectedRecordId)

  const stockMenuItems = [
    {
      key: 'stock-check',
      label: t('STOCK_CHECK'),
      onClick: () => handleStockAction('Stock Check')
    },
    {
      key: 'new-delivery',
      label: t('NEW_DELIVERY_RECEIVED'),
      onClick: () => handleStockAction('New Delivery Received')
    },
    {
      key: 'disposal',
      label: t('DISPOSAL'),
      onClick: () => handleStockAction('Disposal')
    }
  ]

  const handleBreadcrumbClick = () => {
    navigate(`/residents/${residentId}/emar/stock`)
  }

  const tableColumns = createTableColumns(
    t,
    handleViewAdministrationRecord,
    handleViewCommentRecord,
    handleViewRecord,
    handleEditRecord,
    theme.color.neutral.color3
  )

  return (
    <PageWrapper testId="emar-medication-stock-screen-v2">
      <PageHeader
        title={t('STOCK_MEDICATION_NAME', { medicationName: medication.name })}
        breadcrumb={{
          text: t('STOCK'),
          onClick: handleBreadcrumbClick
        }}
        testId="med-stock-header-v2"
      >
        <HeaderActions>
          <ReadMoreLink
            onClick={handleReadMore}
            data-testid="read-more-link-v2"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>

          <DownloadButton
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            data-testid="download-pdf-btn-v2"
          >
            {t('DOWNLOAD_PDF')}
          </DownloadButton>

          <Dropdown
            menu={{ items: stockMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button variant="primary" data-testid="manage-stock-btn-v2">
              {t('MANAGE_STOCK')}
              <Chevron direction="down" />
            </Button>
          </Dropdown>
        </HeaderActions>
      </PageHeader>

      {showAlert && (
        <Banner
          color={colors.red}
          text={t('LOW_STOCK_ALERT')}
          data-testid="emar-stock-alert-banner-v2"
        />
      )}

      <FilterContainer title="">
        <RangePicker
          defaultValue={[startDate, endDate]}
          onChange={handleDateChange}
          data-testid="date-range-picker-v2"
        />
      </FilterContainer>

      <Card title={t('STOCK_LEVEL_CHART')}>
        <LineChart
          data={chartData}
          xAxisLabel={t('DATE')}
          yAxisLabel={t('STOCK_COUNT_UNIT', {
            unit: medication.stockQuantityType
          })}
          startDate={startDate.toISOString()}
          endDate={endDate.toISOString()}
          dateFrame="week"
        />
      </Card>

      <Table
        columns={tableColumns}
        dataSource={logs}
        rowKey="id"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        data-testid="med-stock-logs-table-v2"
        onRow={(record) => ({
          onClick: () => handleViewRecord(record.id),
          style: { cursor: 'pointer' }
        })}
      />

      <StockCheckModal
        open={stockCheckModalOpen}
        medicationName={medication.name}
        expectedStock={medication.expectedStock}
        onCancel={handleStockCheckCancel}
        onSave={handleStockCheckSave}
      />
      <DeliveryModal
        open={deliveryModalOpen}
        medicationName={medication.name}
        expectedStock={medication.expectedStock}
        onCancel={handleDeliveryCancel}
        onSave={handleDeliverySave}
      />
      <DisposalModal
        open={disposalModalOpen}
        medicationName={medication.name}
        expectedStock={medication.expectedStock}
        onCancel={handleDisposalCancel}
        onSave={handleDisposalSave}
      />
      <StockRecordDrawer
        isVisible={drawerOpen}
        onClose={handleCloseDrawer}
        record={selectedRecord}
        recordType={selectedRecordType}
        medicationName={medication.name}
      />
    </PageWrapper>
  )
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ReadMoreLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.primary};
    text-decoration: underline;
    opacity: 0.8;
  }
`

const DownloadButton = styled(Button)`
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    display: none;
  }
`

export default EMARMedicationStockScreenV2
